import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, map, merge, startWith, debounceTime } from 'rxjs';
import { Breakpoint, OS, BREAKPOINT_TABLET, BREAKPOINT_SMALL_DESKTOP, BREAKPOINT_DESKTOP, detectOS } from './ui.config';

function mediaQuery$(query: string) {
  const mql = window.matchMedia(query);
  return fromEvent<MediaQueryListEvent>(mql, 'change').pipe(
    map((e) => e.matches),
    startWith(mql.matches),
  );
}

@Injectable({ providedIn: 'root' })
export class UiService {
  private destroyRef = inject(DestroyRef);

  // Breakpoint — merge all three boundary queries, resolve on any change
  private mqMobile$ = mediaQuery$(`(max-width: ${BREAKPOINT_TABLET - 1}px)`);
  private mqTablet$ = mediaQuery$(
    `(min-width: ${BREAKPOINT_TABLET}px) and (max-width: ${BREAKPOINT_SMALL_DESKTOP - 1}px)`,
  );
  private mqSmallDesktop$ = mediaQuery$(
    `(min-width: ${BREAKPOINT_SMALL_DESKTOP}px) and (max-width: ${BREAKPOINT_DESKTOP - 1}px)`,
  );

  private breakpoint$ = merge(this.mqMobile$, this.mqTablet$, this.mqSmallDesktop$).pipe(
    map(() => this.resolveBreakpoint()),
    startWith(this.resolveBreakpoint()),
  );

  breakpoint = toSignal(this.breakpoint$, { initialValue: this.resolveBreakpoint() });
  isMobile = computed(() => {
    const bp = this.breakpoint();
    return bp === 'mobile' || bp === 'tablet';
  });
  isDesktop = computed(() => !this.isMobile());

  // Viewport dimensions — debounced resize
  private resize$ = fromEvent(window, 'resize').pipe(debounceTime(100));
  viewportWidth = toSignal(this.resize$.pipe(map(() => window.innerWidth)), {
    initialValue: window.innerWidth,
  });
  viewportHeight = toSignal(this.resize$.pipe(map(() => window.innerHeight)), {
    initialValue: window.innerHeight,
  });

  // Orientation
  orientation = toSignal(
    mediaQuery$('(orientation: portrait)').pipe(
      map((portrait) => (portrait ? ('portrait' as const) : ('landscape' as const))),
    ),
    {
      initialValue: window.matchMedia('(orientation: portrait)').matches
        ? ('portrait' as const)
        : ('landscape' as const),
    },
  );

  // Touch capability
  isTouch = toSignal(mediaQuery$('(pointer: coarse)'), {
    initialValue: window.matchMedia('(pointer: coarse)').matches,
  });

  // OS — static, parsed once
  private _os = signal<OS>(detectOS());
  os = this._os.asReadonly();
  isIOS = computed(() => this._os() === 'ios');
  isAndroid = computed(() => this._os() === 'android');

  private resolveBreakpoint(): Breakpoint {
    const w = window.innerWidth;
    if (w < BREAKPOINT_TABLET) return 'mobile';
    if (w < BREAKPOINT_SMALL_DESKTOP) return 'tablet';
    if (w < BREAKPOINT_DESKTOP) return 'small-desktop';
    return 'desktop';
  }
}
